import React, { useRef, useState } from 'react';
import { METRICS_DEFAULT_ACTIVE_IDS } from '../constants';

import type { IMetricsCategory, ICategoryMetricsData } from '@gp/types/metrics';

interface IAnalysisMetricsDataWhenSearchResult {
  categories: IMetricsCategory[];
  metricsData: ICategoryMetricsData;
}

const removeActiveCategoryFirst = (key?: string, keys?: string[]): string[] => {
  if (!Array.isArray(keys) || !keys.length) return [];
  if (!key) return keys;
  const keyIndex = keys.indexOf(key);
  if (keyIndex === -1 || keyIndex === 0) return keys;
  return [keys[keyIndex], ...keys.slice(0, keyIndex), ...keys.slice(keyIndex + 1)];
};

export const analysisMetricsDataWhenSearch = (
  activeId: IMetricsCategory,
  keyword: string,
  metricsData?: ICategoryMetricsData | null,
): IAnalysisMetricsDataWhenSearchResult => {
  const result: IAnalysisMetricsDataWhenSearchResult = {
    categories: [],
    metricsData: {
      project_metrics: [],
      studio_financial_metrics: [],
      common_system_terminology: [],
    },
  };

  if (!metricsData) return result;

  removeActiveCategoryFirst(activeId, Object.keys(metricsData)).forEach((key) => {
    const list = metricsData[key as keyof ICategoryMetricsData];
    const item = list?.filter((item) => item?.metric?.toLowerCase()?.includes(keyword?.toLowerCase()));
    if (Array.isArray(item) && item.length > 0) {
      result.categories.push(key);
    }
    result.metricsData[key as keyof ICategoryMetricsData] = (item || []) as any[];
  });

  return result;
};

export default function useMetricsSearch(props: {
  fullMetricsData?: ICategoryMetricsData | null;
  activeId: IMetricsCategory;
  setActiveId: React.Dispatch<React.SetStateAction<IMetricsCategory>>;
  setAvailableIds: React.Dispatch<React.SetStateAction<IMetricsCategory[]>>;
  setMetricsData: (payload?: ICategoryMetricsData | null) => void;
}) {
  const { fullMetricsData, activeId, setActiveId, setAvailableIds, setMetricsData } = props;

  const searchTimer = useRef<NodeJS.Timeout | null>(null);

  const [keyword, setKeyword] = useState('');

  const handleKeywordSearch = async (keyword?: string) => {
    const keywordTmp = keyword?.trim() || '';
    setKeyword(keywordTmp);
    if (!keywordTmp) {
      handleKeywordClear();
      return [];
    }
    const { categories, metricsData } = analysisMetricsDataWhenSearch(activeId, keywordTmp, fullMetricsData);
    const category = categories?.[0] as IMetricsCategory;
    if (category) {
      setActiveId(category);
    }
    setAvailableIds(categories);
    setMetricsData(metricsData);
    return metricsData[category as keyof ICategoryMetricsData] || [];
  };

  const handleKeywordChange = (payload?: string) => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
      searchTimer.current = null;
    }

    const payloadTmp = payload || '';
    setKeyword(payloadTmp);

    searchTimer.current = setTimeout(() => {
      handleKeywordSearch(payloadTmp);
    }, 800);
  };

  const handleKeywordClear = () => {
    setKeyword('');
    setAvailableIds(METRICS_DEFAULT_ACTIVE_IDS);
    setMetricsData(fullMetricsData);
  };

  return {
    keyword,
    onKeywordSearch: handleKeywordSearch,
    onKeywordChange: handleKeywordChange,
    onKeywordClear: handleKeywordClear,
  };
}
